import { useCallback, useEffect, useRef, useState } from 'react'

function isAbortError(error: unknown): boolean {
  if (!(error instanceof Error)) return false

  return error.name === 'AbortError' || error.message === 'The operation was aborted.'
}

export function useFetchAllPages<T>(loadAll: () => Promise<T[]>): {
  items: T[]
  loading: boolean
  error?: Error
  reload: () => Promise<void>
} {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const requestIdRef = useRef(0)

  const reload = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(undefined)

    try {
      const allItems = await loadAll()
      if (requestId !== requestIdRef.current) return
      setItems(allItems)
    } catch (err) {
      if (requestId !== requestIdRef.current || isAbortError(err)) return
      setItems([])
      setError(err instanceof Error ? err : new Error(String(err)))
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [loadAll])

  useEffect(() => {
    reload()
  }, [reload])

  return { items, loading, error, reload }
}
